import moment from 'moment';
import FormatPhoneNumber from 'components/common/FormatPhoneNumber';

/**
 * Read helpers for registration records. The list row (§5) carries
 * { id, first_name, last_name, email, phone, group_id, requested_role_id,
 * facilities, status, created }; the detail call adds address / IP / review
 * fields. Field names are read defensively so small backend naming
 * differences don't blank the UI.
 */

export const getFullName = record => {
  const name = [record?.first_name, record?.last_name]
    .map(part => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
  return name || record?.name || record?.email || 'Unnamed applicant';
};

export const getInitials = record => {
  const first = String(record?.first_name || '').trim();
  const last = String(record?.last_name || '').trim();
  const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  if (initials) {
    return initials;
  }
  return String(record?.email || '?')
    .charAt(0)
    .toUpperCase();
};

export const formatPhone = phone => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  return digits.length === 10 ? FormatPhoneNumber(digits) : String(phone);
};

const DATE_FORMATS = [
  moment.ISO_8601,
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DD',
  'MM/DD/YYYY hh:mm A',
  'MM/DD/YYYY'
];

export const toMoment = value => {
  if (!value) {
    return null;
  }
  const parsed = moment(value, DATE_FORMATS);
  return parsed.isValid() ? parsed : null;
};

export const formatDateTime = value => {
  const parsed = toMoment(value);
  return parsed ? parsed.format('MMM D, YYYY [at] h:mm A') : '';
};

export const formatRelative = value => {
  const parsed = toMoment(value);
  return parsed ? parsed.fromNow() : '';
};

export const getAgeInHours = value => {
  const parsed = toMoment(value);
  return parsed ? moment().diff(parsed, 'hours') : null;
};

export const getSubmittedAt = record =>
  record?.created ??
  record?.created_at ??
  record?.submitted_at ??
  record?.createdAt ??
  null;

export const getReviewedAt = record =>
  record?.reviewed_at ??
  record?.approved_at ??
  record?.rejected_at ??
  record?.updated_at ??
  record?.updated ??
  null;

export const getReviewerName = record => {
  const name =
    record?.reviewed_by_name ??
    record?.reviewer_name ??
    record?.approved_by_name ??
    record?.rejected_by_name;
  if (name) {
    return name;
  }
  return record?.reviewed_by ? `User #${record.reviewed_by}` : '';
};

export const getIpAddress = record =>
  record?.ip_address ??
  record?.ip ??
  record?.submitted_ip ??
  record?.request_ip ??
  '';

export const getUserAgent = record =>
  record?.user_agent ?? record?.userAgent ?? '';

export const getRequestedRoleId = record =>
  Number(
    record?.requested_role_id ?? record?.requested_role ?? record?.role_id
  ) || null;

export const getApprovedUserId = record =>
  record?.approved_user_id ??
  record?.created_user_id ??
  record?.user_id ??
  null;

/** Does the request carry a group (organization) id? */
export const hasGroup = record => {
  const id = record?.group_id;
  return (
    id !== undefined &&
    id !== null &&
    String(id).trim() !== '' &&
    String(id) !== '0'
  );
};

/**
 * Organization name the applicant typed on /register — only asked when their
 * email domain didn't match one of our clients.
 */
export const getTypedOrganization = record =>
  String(
    record?.organization_name ?? record?.org_name ?? record?.organization ?? ''
  ).trim();

export const getRejectionReason = record =>
  record?.rejection_reason ?? record?.reason ?? '';

export const getStatus = record =>
  String(record?.status || 'pending').toLowerCase();

const parseList = value => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
      return value.split(',');
    }
  }
  if (typeof value === 'number') {
    return [value];
  }
  return [];
};

/**
 * Requested facilities → [{ id, name }]. Accepts ids, id strings, JSON strings
 * or objects; names come from the record itself, else from `lookupName(id)`.
 * NOTE: this is the applicant's REQUEST, never a grant (§5, backend D8).
 */
export const getRequestedFacilities = (record, lookupName = () => '') =>
  parseList(record?.facilities ?? record?.requested_facilities)
    .map(item => {
      if (item && typeof item === 'object') {
        const id = item.id ?? item.facility_id ?? item.value;
        return {
          id,
          name:
            item.name ?? item.facility_name ?? item.key ?? lookupName(id) ?? ''
        };
      }
      const id = String(item).trim();
      return { id, name: lookupName(id) || '' };
    })
    .filter(
      facility =>
        facility.id !== '' && facility.id !== undefined && facility.id !== null
    )
    .map(facility => ({
      id: facility.id,
      name: facility.name || `Facility #${facility.id}`
    }));

/** Stable pick of a colour tone for someone's avatar. */
const AVATAR_TONES = [
  'primary',
  'success',
  'warning',
  'info',
  'purple',
  'pink',
  'teal'
];

export const getAvatarTone = seed => {
  const textSeed = String(seed || '');
  let hash = 0;
  for (let index = 0; index < textSeed.length; index += 1) {
    hash = (hash * 31 + textSeed.charCodeAt(index)) % 2147483647;
  }
  return AVATAR_TONES[hash % AVATAR_TONES.length];
};
